/**
 * Shared frame/line environments and the per-note scratch result of the notes module (W1-06). See noteGeom.ts for
 * the placement formulas and holds.ts for the hold slicing.
 */

import type { LineId } from '../../model/types';
import type { Mat2D } from '../../util/types';
import type { EngineCompileContext, JudgeView, PhiraPlayerSettings, SkinMetrics } from '../types';
import { NOTE_WIDTH_RATIO_BASE } from './constants';
import type { LineNotesRt } from './runtime';
import { NOTE_DRAG, NOTE_FLICK, NOTE_HOLD } from './runtime';

/** NOTE_TEX_SLOTS indices. */
export const SLOT_CLICK = 0;
export const SLOT_HOLD = 1;
export const SLOT_FLICK = 2;
export const SLOT_DRAG = 3;
/** Offset of the MH variant of a slot (click_mh = 4, ...). */
export const SLOT_MH_OFFSET = 4;
export const SLOT_HOLD_BODY = 8;
export const SLOT_HOLD_BODY_MH = 9;

/** Base texture slot of an RPE note type. */
export function baseSlotOf(type: number): number {
  return type === NOTE_HOLD
    ? SLOT_HOLD
    : type === NOTE_FLICK
      ? SLOT_FLICK
      : type === NOTE_DRAG
        ? SLOT_DRAG
        : SLOT_CLICK;
}

/** Frame-wide inputs of the note evaluation. */
export interface FrameEnv {
  readonly t: number;
  readonly ar: number;
  readonly resAlpha: number;
  readonly player: PhiraPlayerSettings;
  readonly skin: SkinMetrics;
  readonly judge: JudgeView | null;
  readonly holdPartialCover: boolean;
  readonly noteUniformScale: boolean;
  /** Half width of a normal note. */
  readonly scaleNormal: number;
  /** Half width of a multi-highlighted note (MH ratio from the click textures for every kind). */
  readonly scaleMH: number;
  /** Early-cull bounds (`surelyOffscreen`): the largest half width of any note quad (before the note size). */
  readonly boundHalfW: number;
  /** Largest half height of a non-hold note quad (before the note size). */
  readonly boundHalfH: number;
  /** Largest head / tail height of a hold (before the note size). */
  readonly boundHoldPad: number;
}

/** Plain-note texture slots (click, flick, drag and their MH variants). */
const NON_HOLD_SLOTS = [0, 2, 3, 4, 6, 7];

export function frameEnv(
  t: number,
  ar: number,
  resAlpha: number,
  player: PhiraPlayerSettings,
  skin: SkinMetrics,
  judge: JudgeView | null,
  pkg: { readonly holdPartialCover: boolean; readonly noteUniformScale: boolean },
): FrameEnv {
  const base = NOTE_WIDTH_RATIO_BASE * player.noteScale;
  const [clickW] = skin.tex.click;
  const [clickMhW] = skin.tex.click_mh;
  const scaleMH = base * (clickMhW / clickW);
  const halfW = Math.max(Math.abs(base), Math.abs(scaleMH));
  let halfH = 0;
  for (const slot of NON_HOLD_SLOTS) {
    const [tw, th] = texSize(skin, slot);
    halfH = Math.max(halfH, Math.abs((halfW * th) / tw));
  }
  const pad = (atlas: readonly [number, number], w: number): number =>
    Math.abs((2 * halfW * Math.max(Math.abs(atlas[0]), Math.abs(atlas[1]))) / w);
  return {
    t,
    ar,
    resAlpha,
    player,
    skin,
    judge,
    holdPartialCover: pkg.holdPartialCover,
    noteUniformScale: pkg.noteUniformScale,
    scaleNormal: base,
    scaleMH,
    boundHalfW: halfW,
    boundHalfH: halfH,
    boundHoldPad: Math.max(pad(skin.holdAtlas, skin.tex.hold[0]), pad(skin.holdAtlasMH, skin.tex.hold_mh[0])),
  };
}

/** Per-line inputs (the line state is copied: pooled states must not be retained). */
export interface LineEnv {
  rt: LineNotesRt;
  line: LineId;
  readonly m: [number, number, number, number, number, number];
  lineHeight: number;
  inclineSin: number;
  /** isCover != 1: notes stay visible below the line and after their time. */
  drawBelow: boolean;
  /** Preview dim multiplier (1 = not dimmed). */
  alphaMul: number;
  /**
   * Early cull allowed for holds (`surelyOffscreen`): the line has no yControl and no sizeControl curve, so a
   * hold's placement needs no control lookup. Off (false) unless `setLineCullFlags` ran for the line.
   */
  cullHolds: boolean;
  /** Early cull allowed for other notes: additionally no posControl curve and no incline. */
  cullOthers: boolean;
  /**
   * Every control curve of the line is absent / inert (`ctx.control` would return null for each), so `evalNote`
   * can skip its per-note lookups and use factor 1. False until `setLineCullFlags` ran for the line.
   */
  noControls: boolean;
}

export function makeLineEnv(): LineEnv {
  return {
    rt: undefined as unknown as LineNotesRt,
    line: 0 as LineId,
    m: [1, 0, 0, 1, 0, 0],
    lineHeight: 0,
    inclineSin: 0,
    drawBelow: false,
    alphaMul: 1,
    cullHolds: false,
    cullOthers: false,
    noControls: false,
  };
}

export function setLineEnv(
  le: LineEnv,
  rt: LineNotesRt,
  m: Mat2D,
  lineHeight: number,
  inclineSin: number,
  alphaMul: number,
): LineEnv {
  le.rt = rt;
  le.line = rt.line;
  for (let k = 0; k < 6; k++) le.m[k] = m[k] ?? 0;
  le.lineHeight = lineHeight;
  le.inclineSin = inclineSin;
  le.drawBelow = rt.ir.isCover !== 1;
  le.alphaMul = alphaMul;
  le.cullHolds = false;
  le.cullOthers = false;
  le.noControls = false;
  return le;
}

/**
 * Enables the early cull of `surelyOffscreen` for the line of `le` (after `setLineEnv`): a control curve is either
 * absent for every key or present (`EngineCompileContext.control` returns null exactly for absent / inert curves).
 */
export function setLineCullFlags(le: LineEnv, ctx: Pick<EngineCompileContext, 'control'>): void {
  const hasY = ctx.control(le.line, 'y', 0) !== null;
  const hasSize = ctx.control(le.line, 'size', 0) !== null;
  const plainY = !hasY && !hasSize;
  le.cullHolds = plainY;
  // The pos probe keeps the short-circuit of the cull flag (an inclined line probes no pos curve), so a
  // control-lookup spy sees the same calls as before `noControls` existed.
  const hasPos = plainY && le.inclineSin === 0 ? ctx.control(le.line, 'pos', 0) !== null : true;
  le.cullOthers = plainY && le.inclineSin === 0 && !hasPos;
  le.noControls = plainY && !hasPos && ctx.control(le.line, 'alpha', 0) === null;
}

/** Result of evaluating one note (scratch object, overwritten by the next evaluation). */
export class NoteEval {
  /** Cover rule (rule 5) hides the note. */
  covered = false;
  /** Note alpha is 0 only because of the visibleTime step (the note has a non-zero alpha later). */
  beforeVisible = false;
  /** Final alpha before the byte clamp. */
  alpha = 0;
  alphaByte = 0;
  r = 255;
  g = 255;
  b = 255;
  /** Number of quads in pos/uv/slot (1 for non-holds, 0..3 for holds: body, head, tail). */
  quads = 0;
  readonly pos = new Float64Array(24);
  readonly uv = new Float64Array(12);
  readonly slot = new Uint8Array(3);
  /** Phira's +-1 off-screen cull per quad. */
  readonly offscreen = new Uint8Array(3);
  /** Outline over every part (ghosts), same corner order as the quads. */
  readonly outline = new Float64Array(8);
  /** yControl value at the note's key (null = no yControl curve on the line). */
  ctrlY: number | null = null;
  /** Local distance of a non-hold's centre from the line on its own side (mirrored for below notes). */
  localY = 0;
}

/** Writes the 4 world corners of local rect [x0, x1] x [y0, y1] (near edge y0, far edge y1) into `out` at `off`. */
export function writeCorners(
  le: LineEnv,
  fy: number,
  tx: number,
  ty: number,
  sx: number,
  sy: number,
  x0: number,
  x1: number,
  y0: number,
  y1: number,
  out: Float64Array,
  off: number,
): void {
  const [a, b, c, d, e, f] = le.m;
  const lx0 = x0 * sx + tx;
  const lx1 = x1 * sx + tx;
  const ly0 = fy * (y0 * sy + ty);
  const ly1 = fy * (y1 * sy + ty);
  // near-left, near-right, far-right, far-left
  out[off] = a * lx0 + c * ly0 + e;
  out[off + 1] = b * lx0 + d * ly0 + f;
  out[off + 2] = a * lx1 + c * ly0 + e;
  out[off + 3] = b * lx1 + d * ly0 + f;
  out[off + 4] = a * lx1 + c * ly1 + e;
  out[off + 5] = b * lx1 + d * ly1 + f;
  out[off + 6] = a * lx0 + c * ly1 + e;
  out[off + 7] = b * lx0 + d * ly1 + f;
}

/** Pixel size of a texture slot. */
export function texSize(skin: SkinMetrics, slot: number): readonly [number, number] {
  switch (slot) {
    case 0:
      return skin.tex.click;
    case 1:
      return skin.tex.hold;
    case 2:
      return skin.tex.flick;
    case 3:
      return skin.tex.drag;
    case 4:
      return skin.tex.click_mh;
    case 5:
      return skin.tex.hold_mh;
    case 6:
      return skin.tex.flick_mh;
    case 7:
      return skin.tex.drag_mh;
    case 8:
      return skin.tex.holdBody;
    default:
      return skin.tex.holdBodyMh;
  }
}
